import * as React from "react"
import { RefObject } from "react"
import { EraserLineType, ObjectsType, PaintLineType, UserDefinedObjectType } from "./watermark-marker-common";
import * as d3 from "d3"
import { URL } from "app/config/api-config";
import { ID, ReduxList } from "app/types/app-types";
import _debounce from "lodash/debounce";
import {Slider} from '@mui/material';

import {
    GridCell,
    Grid,
    css,
    Theme,
    Icon,
    Switcher,
    Button,
} from "@bdfx/mergado-ui-kit";

import { isJson } from "app/helpers/other";
import { CoordsType } from "shape-entities/components/shape";
import trans from "trans";
import { iconDisabledColor } from "template-edit/components/tools-panel";
import { AlternativeTemplate, SettingsFormType, Template } from "app/types/model-types";
import { RandomProduct } from "shape-edit/types";
import { getTemplateSize } from "shape-edit/forms/helpers";
import PreviewTemplateSVG from "template-edit/containers/preview-template-svg";
import { TemplateGalleryType } from "template-edit/components/template-gallery";
import { DOWN_ARROW_KEY, LEFT_ARROW_KEY, MINUS_KEY, PLUS_KEY, RIGHT_ARROW_KEY, UP_ARROW_KEY } from "app/helpers/keymap";
import { parseTransformAndScale } from "trans/helpers";
import { TemplateOrAlternativeTemplateType } from "template-edit/undoRedoReducers";

export type InvertMaskModeType = 'back_to_original' | 'paint_transparent'
const {round, max, min} = Math

const MIN_ZOOM = .25
const MAX_ZOOM = 40
const ZOOM_STEP = .05
const TRANSLATE_STEP = 10

interface Props {
    closeModal: Function
    saveObjects: Function
    idx: number
    product_id: ID
    image_id: ID
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    image_hash: string
    image_url: string
    image_url_hash: string
    image_width: number
    image_height: number
    invert_user_set_objects?: string
    invert_mask_hash?: string
    invert_mask_mode?: InvertMaskModeType
    settingsForm: SettingsFormType
}

interface State {
    version: number
    mode: InvertMaskModeType
    isSaving: boolean
    objects: ObjectsType
    left: number
    top: number
    scaleX: number
    scaleY: number
    objectType: UserDefinedObjectType
    activeLineIndex: number
    brushRadius: number
    sourceLayerVisible: boolean
    bgremovedLayerVisible: boolean
    maskLayerVisible: boolean
    backgroundLayerVisible: boolean
    sourceOpacity: number
    bgremovedOpacity: number
    maskOpacity: number
    randomProduct: RandomProduct
    showAllLayers: boolean
    zoom: number
    translateX: number
    translateY: number
}

const checkerColor = '#ddd'

class InvertMaskMarker extends React.Component<Props, State> {
    canvasRef: RefObject<any>
    leftImage: RefObject<any>
    zoom

    constructor(props: Props) {
        super(props)

        const altString = props.isAlt ? 'alt-' : ''

        this.state = {
            version: 0,
            isSaving: false,
            objects: this.objectsFromProps(props),
            mode: props.invert_mask_mode ? props.invert_mask_mode : 'back_to_original',
            left: 0,
            top: 0,
            scaleX: 1,
            scaleY: 1,
            objectType: 'points',
            activeLineIndex: 0,
            brushRadius: 10,
            sourceLayerVisible: true,
            bgremovedLayerVisible: true,
            maskLayerVisible: true,
            backgroundLayerVisible: true,
            sourceOpacity: 1,
            bgremovedOpacity: .5,
            maskOpacity: .1,
            randomProduct: {
                id: parseInt(`${props.product_id}`),
                image_id: parseInt(`${props.image_id}`),
                image_link: `${URL.laravelBase}get-bgremoved-${altString}image/${this.props.form.project_id}/${props.image_hash}/${props.form.effect_sensitivity ? props.form.effect_sensitivity : 'medium'}?t=${props.invert_mask_hash}`,
                isAlt: props.isAlt,
                bgremovedImage: !!props.invert_user_set_objects ?
                    `${URL.laravelBase}get-bgremoved-${altString}edited/${this.props.form.project_id}/${props.image_url_hash}?t=${props.invert_mask_hash}` :
                    `${URL.laravelBase}get-bgremoved-${altString}image/${this.props.form.project_id}/${props.image_hash}/${props.form.effect_sensitivity ? props.form.effect_sensitivity : 'medium'}?t=${props.invert_mask_hash}`,
                alternative_images: [],
                width: props.image_width,
                height: props.image_height,
                origWidth: props.image_width,
                origHeight: props.image_height,
                image_hash: props.image_hash,
                serialized_data: [],
                isLoading: false,
                isLoaded: true,
                isError: null,
            },
            showAllLayers: true,
            zoom: 1,
            translateX: 0,
            translateY: 0,
        }

        this.canvasRef = React.createRef()
        this.leftImage = React.createRef()
    }

    componentDidMount() {
        this.updateScale(true)
        this.bindEvents()
        document.addEventListener("keydown", this.handleKeyDown)
        this.updateRandomProduct()

    }

    componentWillUnmount(): void {
        this.props.saveObjects(this.state.objects, this.state.mode)
        document.removeEventListener("keydown", this.handleKeyDown)
    }

    handleWheelZoom = (event) => {
        if (typeof event.transform !== 'undefined') {
            this.handleZoom(event.transform.k, { x: event.transform.x, y: event.transform.y }, true)
        }
    }

    handleRightClickOnCanvas = (event) => {
        event.preventDefault();
        this.handleZoom(1, { x: 0, y: 0 })

        /* const el = this.canvasRef.current
        d3.select(el).call(imageEl.transform, d3.zoomIdentity);    //apply the transform  d3.zoomIdentity.translate(0,0).scale(1) */
    }

    handleZoom = (newZoom: number, translate?: CoordsType, isWheelZoom?: boolean) => {
        const translateX = translate && isWheelZoom ? translate.x : this.state.translateX
        const translateY = translate && isWheelZoom ? translate.y : this.state.translateY

        this.setState({
            zoom: newZoom,
            translateX: translateX,
            translateY: translateY
        })

        const imageEl = this.leftImage.current
        const canvasEl = this.leftImage.current

        if (isWheelZoom) {
            const xoffset = (canvasEl.scrollWidth - imageEl.scrollWidth) / 2
            const yoffset = (canvasEl.scrollHeight - imageEl.scrollHeight) / 2
            imageEl.style.transformOrigin = `${xoffset}px 0px`
        } else {
            imageEl.style.transformOrigin = 'center'
        }

        imageEl.style.transform = `translate(${translateX}px,${translateY}px) scale(${newZoom})`

        this.updateScale(true)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && prevProps.idx !== this.props.idx) {
            this.setState({
                objects: this.objectsFromProps(this.props),
                mode: this.props.invert_mask_mode,
            })
            if (this.state.randomProduct.id !== this.props.product_id) {
                this.updateRandomProduct()
            }
        }

        if (prevProps && (prevProps.invert_mask_hash !== this.props.invert_mask_hash || prevProps.invert_mask_mode !== this.props.invert_mask_mode)) {
            const v = `v${this.props.invert_mask_hash}_${this.props.invert_mask_mode}_${this.state.version}`
            const projectId = this.props.form.project_id
            const altString = this.props.isAlt ? 'alt-' : ''
            this.setState({
                randomProduct: {
                    ...this.state.randomProduct,
                    bgremovedImage: !!this.props.invert_user_set_objects ?
                        `${URL.laravelBase}get-bgremoved-${altString}edited/${projectId}/${this.props.image_url_hash}?v=${v}` :
                        `${URL.laravelBase}get-bgremoved-${altString}image/${projectId}/${this.props.image_hash}/${this.props.form.effect_sensitivity ? this.props.form.effect_sensitivity : 'medium'}?v=${v}`,
                }
            })
        }
    }

    updateRandomProduct = () => {
        const { product_id, form, isAlt, idx } = this.props

        console.debug('DEBUG: todo update random product, toto je dulezite pro refresh masky atd ve state, jinak by se mela do PreviewTemplateSVG protlacit version')

        /* getRandomProduct(form, form.queries, form.id, `${product_id}`)
            .then((response: AxiosResponse<RandomProduct>) => {
                const newRandomProduct: RandomProduct = {
                    ...response.data,
                    image_link: !!this.props.invert_user_set_objects ?
                        `${URL.laravelBase}get-bgremoved-edited/${this.props.form.project_id}/${this.props.image_url_hash}?v=b${this.props.invert_mask_hash}` :
                        `${URL.laravelBase}get-bgremoved-image/${this.props.form.project_id}/${this.props.image_hash}/${this.props.form.effect_sensitivity ? this.props.form.effect_sensitivity : 'medium'}?v=b${this.props.invert_mask_hash}`,
                    isLoading: false,
                    isLoaded: true,
                    isError: null,
                }

                //TODO: do something with the random product???
            })
            .catch((e) => {
                console.debug('get random product error', e)
            }) */
    }

    objectsFromProps = (props: Props): ObjectsType => {
        return props.invert_user_set_objects && isJson(props.invert_user_set_objects) ? JSON.parse(props.invert_user_set_objects) : []
    }

    handleKeyDown = (event) => {
        const { objects, zoom, translateX, translateY } = this.state

        if (event.target.type || (event.target.tagName && event.target.tagName === 'INPUT')) {
            return;
        }

        if (event.ctrlKey) {
            switch (event.key) {
                case 'z':   //undo
                    event.preventDefault()
                    const lastIndex = objects.length - 1
                    if (lastIndex > -1) {
                        const updatedObjects = [...objects].splice(lastIndex, 1)
                        this.setState({ objects: updatedObjects })
                        this.props.saveObjects(updatedObjects, this.state.mode)
                    }
                    break;
                case 's':   //save
                    event.preventDefault()
                    this.props.saveObjects(objects, this.state.mode)
                    break;
            }
        } else {
            const step = 20
            let newZoomIdentity = d3.zoomIdentity
            if ([DOWN_ARROW_KEY, UP_ARROW_KEY, LEFT_ARROW_KEY, RIGHT_ARROW_KEY, PLUS_KEY, MINUS_KEY].includes(event.keyCode)) {
                event.preventDefault()
                const current = parseTransformAndScale(this.leftImage.current.style.transform)
                switch (event.keyCode) {
                    case DOWN_ARROW_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x, current.y + step).scale(current.scale)
                        break
                    case UP_ARROW_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x, current.y - step).scale(current.scale)
                        break
                    case LEFT_ARROW_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x - step, current.y).scale(current.scale)
                        break
                    case RIGHT_ARROW_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x + step, current.y).scale(current.scale)
                        break
                    case PLUS_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x, current.y).scale(current.scale + ZOOM_STEP)
                        break
                    case MINUS_KEY:
                        newZoomIdentity = d3.zoomIdentity.translate(current.x, current.y).scale(current.scale - ZOOM_STEP)
                        break
                }
                d3.select(this.canvasRef.current).call(this.zoom.transform, newZoomIdentity);
            }

            /* switch (event.keyCode) {
                case PLUS_KEY:
                    event.preventDefault()
                    this.handleZoom(zoom + ZOOM_STEP)
                    break
                case MINUS_KEY:
                    event.preventDefault()
                    this.handleZoom(zoom - ZOOM_STEP)
                    break
                case LEFT_ARROW_KEY:
                    event.preventDefault()
                    this.handleZoom(zoom, {x: translateX - TRANSLATE_STEP, y: translateY})
                    break
                case RIGHT_ARROW_KEY:
                    event.preventDefault()
                    this.handleZoom(zoom, {x: translateX + TRANSLATE_STEP, y: translateY})
                    break
                case UP_ARROW_KEY:
                    event.preventDefault()
                    this.handleZoom(zoom, {x: translateX, y: translateY - TRANSLATE_STEP})
                    break
                case DOWN_ARROW_KEY:
                    event.preventDefault()
                    this.handleZoom(zoom, {x: translateX, y: translateY + TRANSLATE_STEP})
                    break
            } */
        }
    }

    bindEvents = () => {
        const el = this.canvasRef.current
        if (el && d3.select(el)) {
            d3.select(el)
                .on("contextmenu", (event) => {
                    this.handleRightClickOnCanvas(event)
                })
                .call(
                    d3.drag()
                        /*.filter((event) => !event.button)   // Specify the filter (default includes !d3.event.ctrKey)*/
                        .filter((event) => event.which === 1)
                        .on("start", this.dragStarted)
                        .on("drag", this.dragged)
                        .on("end", this.dragEnded)
                )


            if (!this.props.settingsForm.disable_wheelzoom) {
                const leftImageEl = this.leftImage.current

                const extentWidth = leftImageEl.clientWidth
                const extentHeight = leftImageEl.clientHeight

                this.zoom = d3.zoom()
                    .extent([[0, 0], [extentWidth, extentHeight]]) //The viewport extent affects several functions: the center of the viewport remains fixed during changes by zoom.scaleBy and zoom.scaleTo; the viewport center and dimensions affect the path chosen by d3.interpolateZoom; and the viewport extent is needed to enforce the optional translate extent.
                    .translateExtent([[0, 0], [extentWidth, extentHeight]])   //The translate extent restricts panning, and may cause translation on zoom out.
                    .scaleExtent([MIN_ZOOM, MAX_ZOOM])
                    .on("zoom", this.handleWheelZoom)
                    .constrain((transform, extent, translateExtent) => {
                        const dx0 = transform.invertX(extent[0][0]) - translateExtent[0][0]
                        const dx1 = transform.invertX(extent[1][0]) - translateExtent[1][0]
                        const dy0 = transform.invertY(extent[0][1]) - translateExtent[0][1]
                        const dy1 = transform.invertY(extent[1][1]) - translateExtent[1][1]
                        return transform.translate(
                            dx1 > dx0 ? (dx0 + dx1) / 2 : min(0, dx0) || max(0, dx1),
                            dy1 > dy0 ? (dy0 + dy1) / 2 : min(0, dy0) || max(0, dy1)
                        );
                    })
                d3.select(el).call(this.zoom)

                console.debug('wheel zoom is ENABLED')
            } else {
                console.debug('wheel zoom is DISABLED')
            }
        }
    }

    getCircleCursor(r: number, objectType: UserDefinedObjectType): string {
        const isEraser = objectType === 'eraser'
        const fillColor = isEraser ? '#ffffff0a' : Theme.steelblue
        const lineColor = '#fff'

        const eraserSVGElementLine = (offset: number, lineColor: string) => `<line x1="${r / 2 + 1}" x2="${r + r / 2 + offset}" y1="${r + offset}" y2="${r + offset}" stroke="${lineColor}" stroke-width="1" fill="${lineColor}" /><line y1="${r / 2 + offset}" y2="${r + r / 2 + offset}" x1="${r + offset}" x2="${r + offset}" stroke="${lineColor}" stroke-width="1" fill="${fillColor}" />`
        const circle = (R: number, fill: string, stroke: string) => `<circle cx="${r}" cy="${r}" r="${R}" stroke="${stroke}" stroke-width="1" fill="${fill}" />`

        const circleEraser = circle(3, 'transparent', lineColor)
        const circleTwo = circle(r - 2, 'transparent', '#ffffff')
        const base64SVG = btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="${2 * r}px" height="${2 * r}px">${circle(r - 1, fillColor, Theme.steelblue)}${isEraser && (eraserSVGElementLine(0, '#fff') + eraserSVGElementLine(1, Theme.steelblue) + eraserSVGElementLine(-1, Theme.steelblue) + circleEraser)}${circleTwo}</svg>`);
        return `url('data:image/svg+xml;base64,${base64SVG}') ${r} ${r}, default`
    }

    updateCursor = (scaledBrushRadius: number, objectType: UserDefinedObjectType): void => {
        this.canvasRef.current.style.cursor = this.getCircleCursor(scaledBrushRadius, objectType)
    }

    dragStarted = (event) => {
        const { objectType, brushRadius, objects } = this.state

        this.updateScale(false)
        const newPoint = this.getNewPointPos(event)

        //add point to line
        const newObject: PaintLineType | EraserLineType = objectType === 'points' ? {
            points: [newPoint],
            radius: brushRadius,
        } : {
            eraser: [newPoint],
            radius: brushRadius,
        }

        const updatedObjects = Array.isArray(objects) ? [...objects, newObject] : [newObject]

        this.setState({
            activeLineIndex: updatedObjects.length - 1,
            objects: updatedObjects
        })
    }

    dragged = (event) => {
        const { objectType, brushRadius, objects, activeLineIndex } = this.state

        const newPoint = this.getNewPointPos(event)

        let updatedObjects = [...objects]
        updatedObjects[activeLineIndex][objectType] = [
            ...objects[activeLineIndex][objectType],
            newPoint
        ]

        this.setState({ objects: updatedObjects })
    }

    dragEnded = (event) => {
        this.props.saveObjects(this.state.objects, this.state.mode)
        this.setState({ version: this.state.version + 1 })
    }

    updateScale = (initBrushRadius: boolean) => {
        const { image_width, image_height } = this.props
        const { objectType } = this.state

        const el = this.canvasRef.current

        if (el) {
            const box = el.getBoundingClientRect()

            const scaleX = image_width / box.width
            const scaleY = image_height / box.height

            this.setState({
                left: box.left,
                top: box.top,
                scaleX: scaleX,
                scaleY: scaleY,
            })

            if (initBrushRadius) {
                const newBrushRadius = round(max(image_width, image_height) / 40)
                this.setState({ brushRadius: newBrushRadius })
                this.updateCursor(newBrushRadius / scaleX, objectType)
            }
        }
    }

    getNewPointPos = (event): CoordsType => {
        const { scaleX, scaleY, left, top } = this.state
        return {
            x: round((event.sourceEvent.clientX - left) * scaleX),
            y: round((event.sourceEvent.clientY - top) * scaleY)
        }
    }

    renderObjects = (objects, foregroundColor = 'black', backgroundColor = 'white', addRadius = 1) => {
        if (objects && Array.isArray(objects)) {
            return objects.map((object: (PaintLineType | EraserLineType), key) => {
                //not points nor eraser?
                if ((!object['points'] || !Array.isArray(object['points'])) && (!object['eraser'] || !Array.isArray(object['eraser']))) {
                    return <React.Fragment key={`paint-${key}`} />
                }

                //check the current type:
                const currentObjectType: UserDefinedObjectType = typeof object['points'] !== "undefined" ? 'points' : typeof object['eraser'] !== "undefined" ? 'eraser' : null
                const colorByType = currentObjectType === 'eraser' ? backgroundColor : foregroundColor
                const r = object.radius + addRadius + (currentObjectType === 'eraser' ? 1 : 0)

                if (object[currentObjectType] && Array.isArray(object[currentObjectType]) && object[currentObjectType].length === 1) {
                    return <circle key={`paint-point-${key}`} cx={object[currentObjectType][0].x} cy={object[currentObjectType][0].y} r={r} fill={colorByType} style={{ pointerEvents: 'none' }} />
                }

                const points = object[currentObjectType].map(point => `${round(point.x)},${round(point.y)}`).join(' ')
                return <React.Fragment key={`paint-line-${key}`}>
                    <polyline
                        points={points}
                        fill="none"
                        stroke={colorByType}
                        strokeWidth={2 * r}
                        strokeLinejoin={'round'}
                        strokeLinecap={'round'}
                        style={{ pointerEvents: 'none' }}
                    />
                </React.Fragment>
            })
        }
        return <React.Fragment />
    }

    handleChangeBrushRadius = (brushRadius: number) => {
        const { scaleX, objectType } = this.state
        this.setState({ brushRadius: brushRadius })
        this.updateCursor(brushRadius / scaleX, objectType)
    }

    handleClearObjects = () => {
        this.setState({ objects: null, objectType: 'points', activeLineIndex: 0 })
        this.props.saveObjects(null, this.state.mode)
        this.setState({ version: this.state.version + 1 })
    }

    toggleType = (objectType: UserDefinedObjectType) => {
        const { brushRadius, scaleX } = this.state
        this.setState({ objectType: objectType })
        this.updateCursor(brushRadius / scaleX, objectType)
    }

    toggleShowAllLayers = () => {
        const { showAllLayers } = this.state
        this.setState({ showAllLayers: !showAllLayers })
    }

    render() {
        const { image_id, image_url_hash, image_width, image_height, image_hash, invert_mask_hash, form, product_id, isAlt } = this.props

        const {
            version, objects, brushRadius, sourceLayerVisible, bgremovedLayerVisible, maskLayerVisible, backgroundLayerVisible,
            objectType, sourceOpacity, bgremovedOpacity, maskOpacity, randomProduct, showAllLayers, mode, zoom, translateX, translateY
        } = this.state

        const maskPathId = `invert-mask-path-${image_id}`
        const clipPathId = `mask-path-${image_id}`

        const altString = isAlt ? 'alt-' : ''

        const bgremovedUrl = `${URL.laravelBase}get-bgremoved-${altString}image/${form.project_id}/${image_hash}/${form.effect_sensitivity ? form.effect_sensitivity : 'medium'}?v=${image_hash}${version}`
        const sourceimageUrl = `${URL.laravelBase}get-source-${altString}image/${form.project_id}/${image_hash}`

        const iconSize = 20

        const templateSize = getTemplateSize(form)

        const templateGallery: TemplateGalleryType = {
            id: null,
            trans: null,
            hash: null,
            width: templateSize.width,
            height: templateSize.height,
            serialized_template: JSON.stringify(form),
            priority: 1,
            groups: null,
            thumb: null,
        }

        const imageGridCellClass = !sourceLayerVisible ? 'checker border' : ''

        return (
            <StyledGrid cols={'90px 1fr 1fr'} gap={'20px'}>
                <GridCell>
                    <LayersGrid cols="22px 80px" rows="50px" gap="2px">
                        <GridCell className="clickable" onClick={() => this.setState({ sourceLayerVisible: !sourceLayerVisible })}><Icon type={sourceLayerVisible ? 'eye' : 'eye-slash'} color={sourceLayerVisible ? Theme.blue : iconDisabledColor} size={16} /></GridCell>
                        <GridCell className="checker border" title={trans('original_image')}>
                            <img className="preview-image" src={sourceimageUrl} />
                        </GridCell>
                        <GridCell col="1/-1">
                            <Slider
                                size={"small"}
                                value={sourceOpacity}
                                onChange={(event, value: number) => this.setState({ sourceOpacity: value })}
                                defaultValue={.5}
                                min={0}
                                max={1}
                                step={.1}
                                valueLabelDisplay="auto"
                            />
                        </GridCell>
                        <GridCell className="clickable" onClick={() => this.setState({ bgremovedLayerVisible: !bgremovedLayerVisible })}><Icon type={bgremovedLayerVisible ? 'eye' : 'eye-slash'} color={bgremovedLayerVisible ? Theme.blue : iconDisabledColor} size={16} /></GridCell>
                        <GridCell className="checker border" title={trans('bgremoved_image')}>
                            <StyledSvg version="1.1" viewBox={`0 0 ${image_width} ${image_height}`} fill={Theme.steelblue}>
                                <defs>
                                    <mask id={`${clipPathId}_x`}>
                                        <rect x="0" y="0" width="100%" height="100%" fill="black" />
                                        {this.renderObjects(objects, 'white', 'black', 1)}
                                    </mask>
                                    <mask id={`${maskPathId}_x`}>
                                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                        {this.renderObjects(objects, 'black', 'white', 0)}
                                    </mask>
                                </defs>
                                <g width={image_width} height={image_height} className="shadow-box">
                                    <image
                                        width={image_width}
                                        height={image_height}
                                        xlinkHref={bgremovedUrl}
                                        style={{ pointerEvents: 'none', opacity: bgremovedOpacity }}
                                        mask={`url(#${maskPathId}_x)`}
                                    />
                                </g>
                            </StyledSvg>
                        </GridCell>

                        <GridCell col="1/-1">
                            <Slider
                                size={"small"}
                                value={bgremovedOpacity}
                                onChange={(event, value: number) => this.setState({ bgremovedOpacity: value })}
                                defaultValue={.5}
                                min={0}
                                max={1}
                                step={.1}
                                valueLabelDisplay="auto"
                            />
                        </GridCell>

                        <GridCell className="clickable" onClick={() => this.setState({ maskLayerVisible: !maskLayerVisible })} ><Icon type={maskLayerVisible ? 'eye' : 'eye-slash'} color={maskLayerVisible ? Theme.blue : iconDisabledColor} size={16} /></GridCell>
                        <GridCell className="checker border" title={trans('clip_mask')}>
                            <StyledSvg version="1.1" viewBox={`0 0 ${image_width} ${image_height}`} className={'preview-image checker'}>
                                {this.renderObjects(objects)}
                            </StyledSvg>
                        </GridCell>
                        <GridCell col="1/-1">
                            <Slider
                                size={"small"}
                                value={maskOpacity}
                                onChange={(event, value: number) => this.setState({ maskOpacity: value })}
                                defaultValue={.5}
                                min={0}
                                max={1}
                                step={.1}
                                valueLabelDisplay="auto"
                            />
                        </GridCell>
                    </LayersGrid>
                </GridCell>
                <GridCell className={imageGridCellClass} style={{ overflow: 'hidden', position: 'relative' }}>
                    <StyledSvg
                        version="1.1"
                        viewBox={`0 0 ${image_width} ${image_height}`}
                        fill={Theme.steelblue} ref={this.leftImage}
                        className={'shadow-box'}
                        style={{ transform: `translate(${translateX}%,${translateY}%) scale(${zoom})` }}
                    >
                        <defs>
                            <mask id={`${clipPathId}_x`}>
                                <rect x="0" y="0" width="100%" height="100%" fill="black" />
                                {this.renderObjects(objects, 'white', 'black', 1)}
                            </mask>
                            <mask id={`${maskPathId}_x`}>
                                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                {this.renderObjects(objects, 'black', 'white', 0)}
                            </mask>
                        </defs>
                        <g width={image_width} height={image_height} className="shadow-box">
                            <rect
                                ref={this.canvasRef}
                                width={image_width}
                                height={image_height}
                                fill={'transparent'}
                            />

                            {sourceLayerVisible &&
                                <image
                                    width={image_width}
                                    height={image_height}
                                    xlinkHref={sourceimageUrl}
                                    style={{ pointerEvents: 'none', opacity: sourceOpacity }}
                                />
                            }

                            {bgremovedLayerVisible &&
                                <image
                                    width={image_width}
                                    height={image_height}
                                    xlinkHref={bgremovedUrl}
                                    style={{ pointerEvents: 'none', opacity: bgremovedOpacity }}
                                    mask={`url(#${maskPathId}_x)`}
                                />
                            }

                            {maskLayerVisible &&
                                <rect
                                    width={image_width}
                                    height={image_height}
                                    mask={`url(#${clipPathId}_x)`}
                                    style={{ pointerEvents: 'none', opacity: maskOpacity }}
                                />
                            }
                        </g>
                    </StyledSvg>

                    <ZoomCell title={trans('zoom_in')} style={{
                        opacity: 0.3,
                        ":hover": {
                            opacity: 1,
                        },
                        pointerEvents: 'auto'
                    }}>
                        <Button color="nocolor" size="tiny"
                            icon={<Icon
                                type={'search-light'}
                                size={"15"}
                                text={`${(round(zoom * 100) / 100).toFixed(2)}×`} color={((zoom !== 1 || translateX !== 0 || translateY !== 0)) ? Theme.blue : iconDisabledColor}
                            />}
                            onClick={() => this.handleZoom(1, { x: 0, y: 0 })}
                            style={{
                                borderRight: "1px solid #ddd",
                                borderRadius: 0,
                            }}
                        />
                        <Button
                            color="nocolor"
                            size="tiny"
                            icon={<Icon type={'plus-light'} size={"13"} color={Theme.blue} />}
                            onClick={() => this.handleZoom(zoom + ZOOM_STEP)}
                        />
                        <Button
                            color="nocolor"
                            size="tiny"
                            icon={<Icon type={'minus-light'} size={"13"} color={Theme.blue} />}
                            onClick={() => this.handleZoom(zoom - ZOOM_STEP)}
                        />
                    </ZoomCell>
                </GridCell>
                <GridCell style={{
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                }} className={imageGridCellClass}>
                    {(randomProduct.id === product_id && showAllLayers) ?
                        <React.Fragment>
                            <PreviewTemplateSVG
                                isAlt={isAlt}
                                templateGallery={templateGallery}
                                randomProduct={randomProduct}
                                style={{
                                    width: 'auto',
                                    height: 'auto',
                                    maxWidth: '100%',
                                    maxHeight: 'calc(100vh - 250px)',
                                    backgroundImage: `linear-gradient(45deg, ${checkerColor} 25%, transparent 25%),linear-gradient(135deg, ${checkerColor} 25%, transparent 25%),linear-gradient(45deg, transparent 75%, ${checkerColor} 75%),linear-gradient(135deg, transparent 75%, ${checkerColor} 75%)`,
                                    backgroundSize: '25px 25px',
                                    backgroundPosition: '0 0, 12.5px 0, 12.5px -12.5px, 0px 12.5px',
                                }}
                            />
                        </React.Fragment> :
                        <React.Fragment>
                            <StyledSvg version="1.1" viewBox={`0 0 ${image_width} ${image_height}`} className={backgroundLayerVisible ? 'checker' : undefined}>
                                <defs>
                                    <mask id={clipPathId}>
                                        <rect x="0" y="0" width="100%" height="100%" fill="black" />
                                        {this.renderObjects(objects, 'white', 'black', 1)}
                                    </mask>

                                    <mask id={maskPathId}>
                                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                        {this.renderObjects(objects, 'black', 'white', 0)}
                                    </mask>
                                </defs>
                                <g width={image_width} height={image_height} className={`shadow-box ${mode === 'paint_transparent'} ? 'checker' : ''`}>
                                    <image
                                        width={image_width}
                                        height={image_height}
                                        xlinkHref={bgremovedUrl}
                                        style={{ pointerEvents: 'none' }}
                                        mask={`url(#${maskPathId})`}
                                    />
                                    {mode === 'back_to_original' &&
                                        <image
                                            width={image_width}
                                            height={image_height}
                                            xlinkHref={sourceimageUrl}
                                            style={{ pointerEvents: 'none' }}
                                            mask={`url(#${clipPathId})`}
                                        />
                                    }
                                    <rect
                                        width={image_width}
                                        height={image_height}
                                        stroke={'#cccccc'}
                                        strokeWidth={4}
                                        fill={'transparent'}
                                    />
                                </g>
                            </StyledSvg>
                        </React.Fragment>
                    }
                </GridCell>

                <GridCell col={'1/-1'}>
                    <Grid cols={'max-content 80px 200px repeat(4, 80px)'} gap={'20px'} rows={"80px"}>
                        <GridCell>
                            <Switcher
                                defaultValue="back_to_original"
                                value={mode}
                                onChange={(value) => {
                                    this.setState({ mode: value })
                                    this.props.saveObjects(this.state.objects, value)
                                    this.setState({ version: this.state.version + 1 })
                                }}
                                items={[
                                    { label: "back_to_original", text: <Icon type="back-left-light" size={12} text={trans('back_to_original')} color="#333" />, value: "back_to_original" },
                                    { label: "paint_transparent", text: <Icon type="eraser-radix" size={12} text={trans('paint_transparent')} color="#333" />, value: "paint_transparent" },
                                ]}
                            />
                        </GridCell>
                        <GridCell />
                        <GridCell>
                            <div className={'slider-title'}>{trans('radius')} [px]</div>
                            <Slider
                                size="small"
                                value={brushRadius}
                                onChange={(event, brushRadius: number) => this.handleChangeBrushRadius(brushRadius)}
                                defaultValue={10}
                                min={round(max(image_width, image_height) / 500)}
                                max={round(max(image_width, image_height) / 20)}
                                valueLabelDisplay="auto"
                            />
                        </GridCell>
                        <GridCell onClick={() => this.toggleType('points')}>
                            <Icon type={'paint-brush'} size={iconSize} color={(objectType === 'points') ? Theme.steelblue : iconDisabledColor} />
                        </GridCell>
                        <GridCell onClick={() => this.toggleType('eraser')}>
                            <Icon type={'eraser'} size={iconSize} color={(objectType === 'eraser') ? Theme.steelblue : iconDisabledColor} />
                        </GridCell>
                        <GridCell onClick={() => this.handleClearObjects()} title={trans('delete')}>
                            <Icon type={'trash'} size={iconSize} color={(objects && objects.length > 0) ? Theme.steelblue : iconDisabledColor} />
                        </GridCell>
                        <GridCell onClick={() => this.toggleShowAllLayers()} title={trans('layers')}>
                            <Icon type={'layers-radix'} size={iconSize} color={showAllLayers ? Theme.steelblue : iconDisabledColor} />
                        </GridCell>
                    </Grid>
                </GridCell>
                <GridCell />
            </StyledGrid>
        )
    }
}

const StyledSvg = css('svg')({
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 250px)',
})

const StyledGrid = css(Grid)({
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
    ' .preview-image': {
        width: 'auto',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: 'calc(100vh - 250px)',
    },
    ' .inline': {
        display: 'flex',
    },
})

const LayersGrid = css(Grid)({
    ' .muk-gridcell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
    ' .border': {
        border: '1px solid #dddddd',
    }
})

const ZoomCell = css('div')({
    position: 'absolute',
    top: 0,
    right: 0,
    boxShadow: '0 0 3px 1px rgba(0,0,0,.3)',
    backgroundColor: '#fff',
    whiteSpace: "nowrap",
    ' .muk-icon__text': {
        margin: '0 !important',
        verticalAlign: "middle",
        fontWeight: "normal",
        fontSize: "11px",
    },
    borderRadius: "5px",
    overflow: "hidden",
    ".muk-button": {
        padding: "5px 10px",
    },
})

export default InvertMaskMarker
